/**
 * useVoiceCall — React hook that exposes Twilio Voice call state and actions.
 *
 * Must be used inside a <VoiceCallProvider>.
 */

import { createContext, useContext } from 'react';
import { AudioDevice, AudioDeviceType, CallSummary, VoiceCallEvent } from '@/lib/twilioVoice';

// ─── Context shape ─────────────────────────────────────────────────────────────

export interface VoiceCallContextValue {
  /** Current call state */
  callState: VoiceCallEvent['state'];
  /** E.164 of the remote party */
  remoteNumber: string;
  /** Human-readable display name (if known) */
  remoteName?: string;
  /** Caller-ID number used for this call */
  callerIdE164?: string;
  /** Twilio call SID */
  callSid?: string;
  /** Whether microphone is muted */
  isMuted: boolean;
  /** Currently active audio output route */
  audioRoute: AudioDeviceType;
  audioRouteError?: string;
  /** All available audio output devices */
  availableAudioDevices: AudioDevice[];
  /** Call quality warnings, if any */
  qualityWarnings: string[];
  /** Elapsed call duration in seconds (increments once per second during 'connected') */
  callDuration: number;
  /** Last error message */
  error?: string;
  /** True on an 'ended' state that dropped uncleanly with redial params remembered — show a Redial button. */
  canRedial?: boolean;
  /**
   * Every live call on this tab, primary first (web dialer call waiting). Length ≤ 1 on native and
   * whenever nothing else is up, so UI that predates call waiting can keep ignoring it entirely;
   * the fields above always describe `calls[0]` (the primary).
   */
  calls: CallSummary[];

  // ── Actions ─────────────────────────────────────────────────────────────────
  /** Start an outbound call */
  makeCall: (toE164: string, callerIdE164: string, displayName?: string, jobId?: string) => Promise<void>;
  /** Re-place the last call after an unclean drop (one-tap Redial). */
  redial: () => Promise<void>;
  /** Start a masked outbound call where the real number is resolved server-side */
  /** callerIdE164: the line to call from; omitted = the first usable one (the job's line when it has one). */
  makeMaskedCall: (jobId: string, contactId: string, which?: 'primary' | 'additional', displayName?: string, callerIdE164?: string) => Promise<void>;
  /** End the active call */
  endCall: () => Promise<void>;
  /** Toggle mute */
  toggleMute: () => Promise<void>;
  /** Route audio to the specified device (type is the icon hint; id targets a specific web device) */
  selectAudioDevice: (type: AudioDeviceType, id?: string) => Promise<void>;
  /** Send a DTMF digit to the active call */
  sendDtmf: (digit: string) => void;

  // ── Call waiting (web dialer only; no-ops on native) ────────────────────────
  /** Answer a specific ringing call — holds the call in progress first, if any. */
  answerCall: (callSid: string) => Promise<void>;
  /** Decline a specific ringing call. Any call already in progress is untouched. */
  declineCall: (callSid: string) => void;
  /** Hold or resume one call. Resuming holds whatever else is live, so this is also how the UI
   *  switches between calls. */
  setHold: (callSid: string, held: boolean) => Promise<void>;
  /** End one specific call (the per-row End in the call list). */
  endCallById: (callSid: string) => Promise<void>;

  /** Open the job attached to a call (from its invoice-number chip on the widget). */
  openJob: (jobId: string) => void;
  /** Close it. The modal lives above the router, so nothing unmounts it on navigation. */
  closeJob: () => void;
  /** Open an estimate overlay (e.g. after converting a job). Same host as openJob. */
  openEstimate: (estimateId: string) => void;
  /** Close the estimate overlay. */
  closeEstimate: () => void;
}

// ─── Context ───────────────────────────────────────────────────────────────────

export const VoiceCallContext = createContext<VoiceCallContextValue | undefined>(undefined);

export function useVoiceCall(): VoiceCallContextValue {
  const ctx = useContext(VoiceCallContext);
  if (!ctx) throw new Error('useVoiceCall must be used inside <VoiceCallProvider>');
  return ctx;
}
